(() => {
  // Türkiye il / ilçe selection for the cart.
  //
  // The customer picks il and ilçe from the official list (assets/
  // wemond-tr-locations.json — the same dataset the Wemond invoice backend
  // validates against). The pair is stored ONLY as two Shopify cart
  // attributes (wemond_province / wemond_district), which Shopify copies to
  // the order. Every theme path into checkout is gated until a valid pair
  // is saved on the cart. The backend re-validates and fails closed, so this
  // gate is a UX layer, not the security boundary.
  if (window.WemondTrLocation) return;

  const ATTR_PROVINCE = 'wemond_province';
  const ATTR_DISTRICT = 'wemond_district';
  const MSG_REQUIRED = 'Devam etmek için il ve ilçenizi seçin.';
  const MSG_SAVE_FAILED = 'Seçiminiz kaydedilemedi. Lütfen tekrar deneyin.';
  const root = () => window.Shopify?.routes?.root || '/';

  // MUST stay identical to foldTrKey in the backend (src/locations/tr-locations.ts).
  const foldTrKey = (value) => String(value ?? '')
    .normalize('NFC')
    .replace(/[İIı]/g, 'i')
    .toLocaleLowerCase('tr-TR')
    .replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ü/g, 'u')
    .replace(/ö/g, 'o').replace(/ç/g, 'c')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const state = {
    dataset: null,
    datasetPromise: null,
    // Last pair confirmed by Shopify (from /cart.js or a successful update).
    saved: { province: '', district: '' },
    // Pair currently shown in the selects (may be ahead of `saved`).
    draft: { province: '', district: '' },
    saving: Promise.resolve(),
    pending: 0,
    touched: false,
    error: ''
  };

  const instances = new Set();

  function datasetUrl() {
    return document.querySelector('[data-wtl-dataset]')?.getAttribute('data-wtl-dataset') || '';
  }

  function loadDataset() {
    if (state.datasetPromise) return state.datasetPromise;
    state.datasetPromise = (async () => {
      const url = datasetUrl();
      if (!url) throw new Error('DATASET_URL_MISSING');
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('DATASET_UNAVAILABLE');
      const json = await response.json();
      const provinces = (json.provinces || []).map(([code, name, districts]) => ({
        code,
        name,
        districts,
        districtKeys: new Map(districts.map((district) => [foldTrKey(district), district]))
      }));
      state.dataset = {
        version: json.version,
        provinces,
        byKey: new Map(provinces.map((province) => [foldTrKey(province.name), province]))
      };
      return state.dataset;
    })();
    state.datasetPromise.catch(() => { state.datasetPromise = null; });
    return state.datasetPromise;
  }

  // Canonical official pair, or null. Never guesses.
  function resolvePair(province, district) {
    if (!state.dataset) return null;
    const entry = state.dataset.byKey.get(foldTrKey(province));
    const canonical = entry?.districtKeys.get(foldTrKey(district));
    if (!entry || !canonical || !foldTrKey(province) || !foldTrKey(district)) return null;
    return { province: entry.name, district: canonical };
  }

  function provinceEntry(province) {
    return state.dataset?.byKey.get(foldTrKey(province)) || null;
  }

  function savedIsValid() {
    return state.pending === 0 && Boolean(resolvePair(state.saved.province, state.saved.district));
  }

  function syncDocumentState() {
    const valid = savedIsValid();
    document.querySelectorAll('[data-wtl-requires]').forEach((node) => {
      node.toggleAttribute('data-wtl-ok', valid);
    });
    document.querySelectorAll('[data-wtl-checkout]').forEach((node) => {
      node.toggleAttribute('data-wtl-blocked', !valid);
      const describedBy = node.getAttribute('data-wtl-checkout');
      if (!valid && describedBy) node.setAttribute('aria-describedby', describedBy);
      else node.removeAttribute('aria-describedby');
    });
  }

  function renderAll() {
    instances.forEach((instance) => instance.render());
    syncDocumentState();
  }

  async function fetchCart() {
    const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('CART_UNAVAILABLE');
    return response.json();
  }

  function adoptCartAttributes(cart) {
    const attributes = cart?.attributes || {};
    state.saved = {
      province: String(attributes[ATTR_PROVINCE] || ''),
      district: String(attributes[ATTR_DISTRICT] || '')
    };
    if (state.pending === 0 && !state.touched) state.draft = { ...state.saved };
  }

  // Serialized cart attribute writes. An empty value removes the attribute.
  function persist(province, district) {
    state.pending += 1;
    state.error = '';
    const run = async () => {
      try {
        const response = await fetch(`${root()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [ATTR_PROVINCE]: province, [ATTR_DISTRICT]: district } })
        });
        if (!response.ok) throw new Error('CART_UPDATE_FAILED');
        // /cart.js is authoritative (same rule as the mini cart).
        const cart = await fetchCart();
        const attributes = cart?.attributes || {};
        state.saved = {
          province: String(attributes[ATTR_PROVINCE] || ''),
          district: String(attributes[ATTR_DISTRICT] || '')
        };
        if (state.saved.province !== province || state.saved.district !== district) throw new Error('CART_ATTRIBUTES_NOT_PERSISTED');
      } catch (_) {
        state.error = MSG_SAVE_FAILED;
      } finally {
        state.pending -= 1;
        renderAll();
      }
    };
    state.saving = state.saving.then(run, run);
    renderAll();
    return state.saving;
  }

  function choose(province, district) {
    const entry = provinceEntry(province);
    const nextProvince = entry ? entry.name : '';
    const nextDistrict = entry && resolvePair(nextProvince, district) ? resolvePair(nextProvince, district).district : '';
    state.draft = { province: nextProvince, district: nextDistrict };
    state.touched = true;
    state.error = '';
    instances.forEach((instance) => instance.clearPrompt());
    // Only complete, valid pairs (or a full reset) are written to Shopify.
    // A province without district clears any previously saved pair so the
    // cart can never keep a stale combination.
    if (nextProvince && nextDistrict) return persist(nextProvince, nextDistrict);
    if (state.saved.province || state.saved.district) return persist('', '');
    renderAll();
    return Promise.resolve();
  }

  class WemondTrLocation extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.provinceSelect = this.querySelector('[data-wtl-province]');
      this.districtSelect = this.querySelector('[data-wtl-district]');
      this.errorNode = this.querySelector('[data-wtl-error]');
      this.prompted = false;
      instances.add(this);

      // Server-rendered cart attributes give an immediate, shift-free state.
      if (!state.dataset && state.pending === 0 && !state.saved.province) {
        state.saved = { province: this.dataset.province || '', district: this.dataset.district || '' };
        state.draft = { ...state.saved };
      }

      this.provinceSelect?.addEventListener('change', () => choose(this.provinceSelect.value, ''));
      this.districtSelect?.addEventListener('change', () => choose(this.provinceSelect?.value || state.draft.province, this.districtSelect.value));

      loadDataset().then(() => {
        // Normalize an older/differently spelled saved pair to the canonical one.
        const canonical = resolvePair(state.saved.province, state.saved.district);
        if (canonical && (canonical.province !== state.saved.province || canonical.district !== state.saved.district)) {
          choose(canonical.province, canonical.district);
        } else if (!canonical && (state.saved.province || state.saved.district) && state.pending === 0) {
          // Unknown / partial pair on the cart: keep the valid province only.
          choose(state.saved.province, '');
        }
        renderAll();
      }).catch(() => {
        state.error = 'İl listesi yüklenemedi. Lütfen sayfayı yenileyin.';
        renderAll();
      });
      this.render();
    }

    disconnectedCallback() {
      instances.delete(this);
    }

    option(value, label, selected) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      if (selected) option.selected = true;
      return option;
    }

    render() {
      const province = this.provinceSelect;
      const district = this.districtSelect;
      if (!province || !district) return;

      if (state.dataset && province.dataset.populated !== 'true') {
        province.replaceChildren(
          this.option('', 'İl seçin', false),
          ...state.dataset.provinces.map((entry) => this.option(entry.name, entry.name, false))
        );
        province.dataset.populated = 'true';
      }

      const entry = provinceEntry(state.draft.province);
      if (state.dataset) {
        province.value = entry ? entry.name : '';
        const key = entry ? entry.name : '';
        if (district.dataset.forProvince !== key) {
          district.replaceChildren(
            this.option('', entry ? 'İlçe seçin' : 'Önce il seçin', false),
            ...(entry ? entry.districts.map((name) => this.option(name, name, false)) : [])
          );
          district.dataset.forProvince = key;
        }
        district.value = entry && resolvePair(entry.name, state.draft.district) ? resolvePair(entry.name, state.draft.district).district : '';
        district.disabled = !entry;
      }

      const busy = state.pending > 0;
      this.toggleAttribute('data-busy', busy);
      const message = state.error || (this.prompted && !resolvePair(state.draft.province, state.draft.district) ? MSG_REQUIRED : '');
      if (this.errorNode) this.errorNode.textContent = message;
      const provinceMissing = this.prompted && !entry;
      const districtMissing = this.prompted && entry && !resolvePair(entry.name, state.draft.district);
      province.setAttribute('aria-invalid', String(Boolean(provinceMissing)));
      district.setAttribute('aria-invalid', String(Boolean(districtMissing)));
      this.classList.toggle('is-invalid', Boolean(message));
    }

    clearPrompt() {
      this.prompted = false;
    }

    prompt({ focus = true } = {}) {
      this.prompted = true;
      this.render();
      if (!focus) return;
      const target = !provinceEntry(state.draft.province) ? this.provinceSelect : this.districtSelect;
      try { target?.focus({ preventScroll: false }); } catch (_) { target?.focus(); }
      this.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }

    isVisible() {
      return Boolean(this.offsetParent || this.getClientRects().length);
    }
  }

  if (!customElements.get('wemond-tr-location')) customElements.define('wemond-tr-location', WemondTrLocation);

  async function promptSelection() {
    const visible = [...instances].find((instance) => instance.isVisible());
    if (visible) {
      instances.forEach((instance) => instance.prompt({ focus: instance === visible }));
      return;
    }
    // No selector on screen (product page, experience builder…): open the
    // mini cart, which always carries one.
    try { await window.WemondMiniCart?.open?.(); } catch (_) {}
    window.setTimeout(() => {
      const opened = [...instances].find((instance) => instance.isVisible());
      instances.forEach((instance) => instance.prompt({ focus: instance === opened }));
    }, 260);
  }

  // Authoritative check right before leaving for checkout: waits for pending
  // writes and re-reads the cart from Shopify.
  async function ensureReady() {
    try { await state.saving; } catch (_) {}
    try {
      await loadDataset();
      adoptCartAttributes(await fetchCart());
    } catch (_) {
      return false;
    }
    renderAll();
    return savedIsValid();
  }

  async function goToCheckout() {
    if (await ensureReady()) {
      window.location.assign(`${root()}checkout`);
      return true;
    }
    promptSelection();
    return false;
  }

  function isCheckoutHref(anchor) {
    const href = anchor.getAttribute('href');
    if (!href) return false;
    try {
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return false;
      const path = url.pathname.replace(/\/+$/, '');
      const prefix = root().replace(/\/+$/, '');
      return path === `${prefix}/checkout` || path === '/checkout' || path.startsWith('/checkouts/');
    } catch (_) {
      return false;
    }
  }

  function gate(event, proceed) {
    if (savedIsValid()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (state.pending > 0 && proceed) {
      // A valid choice may still be saving: finish, then continue.
      ensureReady().then((ok) => (ok ? proceed() : promptSelection()));
      return;
    }
    promptSelection();
  }

  // Capture phase: runs before Shopify / theme handlers (including the
  // shadow-DOM accelerated checkout buttons, whose events retarget to the host).
  document.addEventListener('click', (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const accelerated = path.find((node) => node?.matches?.('shopify-accelerated-checkout-cart, shopify-accelerated-checkout, .additional-checkout-buttons, .shopify-payment-button'));
    if (accelerated) {
      gate(event, null);
      return;
    }
    const anchor = event.target.closest?.('a[href]');
    if (anchor && isCheckoutHref(anchor)) {
      gate(event, () => window.location.assign(anchor.href));
      return;
    }
    const button = event.target.closest?.('button[name="checkout"], input[name="checkout"]');
    if (button) {
      gate(event, () => goToCheckout());
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = event.submitter;
    const action = form?.getAttribute?.('action') || '';
    const toCheckout = submitter?.name === 'checkout' || /\/checkout(?:[/?#]|$)/.test(action);
    if (toCheckout) gate(event, () => goToCheckout());
  }, true);

  // Horizon re-renders cart sections after line changes; re-apply gate state.
  document.addEventListener('shopify:cart:lines-update', () => window.setTimeout(renderAll, 450));

  window.WemondTrLocation = {
    goToCheckout,
    ensureReady,
    isValid: savedIsValid,
    prompt: promptSelection,
    foldTrKey
  };

  // Initial cart state (also covers pages without a selector, e.g. product
  // pages, so the gate knows a valid pair saved on an earlier page).
  Promise.all([loadDataset(), fetchCart()]).then(([, cart]) => {
    if (state.pending === 0) adoptCartAttributes(cart);
    renderAll();
  }).catch(() => renderAll());
})();
